import { cva } from 'class-variance-authority';
import { ElementType } from 'react';
import { TextProps, TextSizeVariant } from './text.types';

export const textVariants = {
  color: {
    contentDefault: 'text-content-default',
    contentSubdued: 'text-content-subdued',
    contentDisabled: 'text-content-disabled',
    contentInverse: 'text-content-inverse',
    contentPositive: 'text-content-positive',
    contentWarning: 'text-content-warning',
    contentNegative: 'text-content-negative',
    contentInfo: 'text-content-info',
    primary: 'text-primary',
    inherit: 'text-inherit',
  },
  size: {
    // Heading
    headingXL: 'text-heading-xl',
    headingL: 'text-heading-l',
    headingM: 'text-heading-m',
    headingS: 'text-heading-s',
    headingXS: 'text-heading-xs',
    // Body
    bodyS: 'text-body-s',
    bodyL: 'text-body-l',
    // Button
    buttonL: 'text-button-l',
    buttonS: 'text-button-s',
    // Label
    label: 'text-label',
    labelStrong: 'text-label-strong',
  },
  select: {
    none: 'select-none',
    text: 'select-text',
    all: 'select-all',
    auto: 'select-auto',
  },
  overflow: {
    default: '',
    truncate: 'truncate',
    ellipsis: 'text-ellipsis',
    clip: 'text-clip',
  },
  whiteSpace: {
    default: '',
    normal: 'whitespace-normal',
    nowrap: 'whitespace-nowrap',
    pre: 'whitespace-pre',
    preLine: 'whitespace-pre-line',
    preWrap: 'whitespace-pre-wrap',
    breakSpaces: 'whitespace-break-spaces',
  },
  sensitive: {
    true: 'sensitive',
    false: '',
  },
} as const;

export const ElementSizeMapping: Partial<Record<TextSizeVariant, ElementType>> =
  {
    headingXL: 'h1',
    headingL: 'h2',
    headingM: 'h3',
    headingS: 'h4',
    headingXS: 'h4',
    bodyL: 'p',
    bodyS: 'p',
  };

export const text = cva(undefined, {
  variants: textVariants,
  defaultVariants: {
    size: 'bodyS',
    color: 'contentDefault',
    select: 'auto',
  },
});

const DEFAULT_ELEMENT = ElementSizeMapping['bodyL']!;

export const Text = <E extends ElementType = typeof DEFAULT_ELEMENT>({
  size,
  color,
  select,
  overflow,
  sensitive,
  whiteSpace,
  children,
  className = '',
  as,
  ref,
  ...props
}: TextProps<E>) => {
  const mappedElement = size ? ElementSizeMapping[size] : undefined;
  const Component = as ?? mappedElement ?? DEFAULT_ELEMENT;

  return (
    <Component
      ref={ref}
      className={`${text({ size, color, select, overflow, whiteSpace, sensitive })} ${className}`.trim()}
      {...props}
    >
      {children}
    </Component>
  );
};
